import { useEffect, useRef, useState } from "react";
import { Flag, CheckSquare, Square, ChevronLeft, ChevronRight, Clock, Send, Volume2, VolumeX, FileText, Loader2 } from "lucide-react";
import { canSpeak, speak, stopSpeaking, prefetchSpeech, speechState, onSpeechState } from "@/lib/speech";

export const squareColor = (st) => {
  const s = st || {};
  if ((s.flagged || []).length) return "bg-[#E11D48] border-[#E11D48]";
  if ((s.checked || []).length || s.touched) return "bg-[#34D399] border-[#34D399]";
  return "bg-white border-white";
};

export function TaskStrip({ screen, state, onToggleCheck, onToggleFlag, readAloud }) {
  const checked = new Set(state?.checked || []);
  const flagged = new Set(state?.flagged || []);
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid={`task-strip-${screen.id}`}>
      {screen.tasks.map((t, i) => {
        const done = checked.has(t.id), flag = flagged.has(t.id);
        return (
          <div key={t.id} data-testid={`ut-task-${screen.id}-${t.id}`} className={`rounded-xl border px-3 py-2.5 text-sm flex items-start gap-2 ${flag ? "border-[#E11D48]/60 bg-[#E11D48]/10" : done ? "border-[#34D399]/50 bg-[#34D399]/10" : "border-white/15 bg-white/[0.03]"}`}>
            <button data-testid={`ut-check-${screen.id}-${t.id}`} onClick={() => onToggleCheck(t.id)} title={done ? "Mark as not done" : "I think this is done"} className="mt-0.5 shrink-0 text-slate-200 hover:text-[#34D399]">
              {done ? <CheckSquare className="w-5 h-5 text-[#34D399]" /> : <Square className="w-5 h-5" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">Task {i + 1} · Block {t.block}</p>
              <p className="leading-snug text-slate-100">{t.label}</p>
            </div>
            {readAloud && canSpeak() && (
              <button data-testid={`ut-speak-${screen.id}-${t.id}`} onClick={() => speak(`Task ${i + 1}. ${t.label}`)} title="Read this task aloud" className="shrink-0 text-[#22D3EE] hover:text-[#67E8F9]">
                <Volume2 className="w-4 h-4" />
              </button>
            )}
            <button data-testid={`ut-flag-${screen.id}-${t.id}`} onClick={() => onToggleFlag(t.id)} title={flag ? "Remove flag" : "Flag to come back to this"} className={`shrink-0 ${flag ? "text-[#E11D48]" : "text-slate-500 hover:text-[#E11D48]"}`}>
              <Flag className={`w-4 h-4 ${flag ? "fill-current" : ""}`} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export const docText = (doc) => {
  if (!doc) return "";
  if (doc.slides) return doc.slides.map((s, i) => `Slide ${i + 1}: ${s.title || "untitled"}. ${(s.bullets || []).filter(Boolean).join(". ")}${s.body ? ". " + s.body : ""}`).join(". ");
  const parts = [];
  for (const b of doc.blocks || []) {
    if (b.type === "table" && b.cells) parts.push((b.cells || []).flat().filter(Boolean).join(", "));
    else if (b.text) parts.push(b.text);
  }
  return parts.join(". ").replace(/\.\./g, ".");
};

export function ReadAloudBar({ screen, index, doc }) {
  const [st, setSt] = useState(speechState());
  useEffect(() => onSpeechState(setSt), []);
  const tasksText = `Screen ${index + 1}: ${screen.title}. ${screen.intro} ` + screen.tasks.map((t, i) => `Task ${i + 1}: ${t.label}.`).join(" ");
  const document = `The document says: ${docText(doc)}`;
  useEffect(() => { prefetchSpeech(tasksText); prefetchSpeech(`The document says: ${docText(screen.doc)}`); }, [tasksText, screen.doc]);
  if (!canSpeak()) return null;
  const busy = st === "loading";
  return (
    <div className="flex items-center gap-2" data-testid="ut-readaloud-bar">
      <button data-testid="ut-read-all-btn" onClick={() => speak(tasksText)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#22D3EE]/50 text-[#22D3EE] text-xs hover:bg-[#22D3EE]/10 disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />} Read tasks
      </button>
      <button data-testid="ut-read-doc-btn" onClick={() => speak(document)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#22D3EE]/50 text-[#22D3EE] text-xs hover:bg-[#22D3EE]/10 disabled:opacity-60">
        <FileText className="w-3.5 h-3.5" /> Read document
      </button>
      <button data-testid="ut-stop-read-btn" onClick={stopSpeaking} title="Stop reading" className={`p-1.5 rounded-full border ${st === "speaking" ? "border-[#E11D48]/60 text-[#E11D48]" : "border-white/15 text-slate-400"} hover:text-slate-100`}><VolumeX className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export function Timer({ deadline, onExpire }) {
  const [left, setLeft] = useState(() => (deadline ? Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000)) : null));
  const fired = useRef(false);
  useEffect(() => {
    if (!deadline) return undefined;
    const iv = setInterval(() => setLeft(Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000))), 1000);
    return () => clearInterval(iv);
  }, [deadline]);
  useEffect(() => { if (deadline && left === 0 && !fired.current) { fired.current = true; onExpire?.(); } }, [left, onExpire, deadline]);
  if (!deadline) {
    return <span data-testid="ut-timer" className="inline-flex items-center gap-1.5 font-mono-data text-sm text-[#34D399] px-2.5 py-1 rounded-full border border-[#34D399]/40" title="Timer suspended (IEP accommodation)"><Clock className="w-4 h-4" /> Untimed</span>;
  }
  const m = Math.floor(left / 60), s = left % 60;
  const urgent = left <= 300;
  return (
    <span data-testid="ut-timer" className={`inline-flex items-center gap-1.5 font-mono-data text-lg ${urgent ? "text-[#E11D48]" : "text-primary"}`}>
      <Clock className="w-4 h-4" /> {m}:{String(s).padStart(2, "0")}
    </span>
  );
}

export function BottomBar({ index, total, states, screens, onGo, onSubmit, submitting }) {
  return (
    <div className="border-t border-white/10 bg-[#070c1a] px-4 py-3 flex items-center justify-between gap-4">
      <button data-testid="ut-back-btn" disabled={index === 0} onClick={() => onGo(index - 1)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-white/15 text-sm disabled:opacity-30 hover:bg-white/5"><ChevronLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-2" data-testid="ut-progress-squares">
        {screens.map((s, i) => (
          <button key={s.id} data-testid={`ut-square-${i + 1}`} onClick={() => onGo(i)} title={`Screen ${i + 1}: ${s.title}`}
            className={`w-6 h-6 rounded-sm border-2 transition-transform hover:scale-110 ${squareColor(states[s.id])} ${i === index ? "ring-2 ring-offset-2 ring-offset-[#070c1a] ring-[#22D3EE]" : ""}`} />
        ))}
      </div>
      {index < total - 1 ? (
        <button data-testid="ut-forward-btn" onClick={() => onGo(index + 1)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#22D3EE] text-[#04121f] text-sm font-medium hover:bg-[#67E8F9]">Forward <ChevronRight className="w-4 h-4" /></button>
      ) : (
        <button data-testid="ut-submit-btn" onClick={onSubmit} disabled={submitting} className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60"><Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit Unit Task"}</button>
      )}
    </div>
  );
}
